<template>
  <modal v-if="showModal" :showCloseBtn="true" @leave="$emit('close')">
    <div class="w-full flex flex-col">
      <h1 class="text-base mb-4 font-semibold">Upload Custom Logo Animation</h1>
      <div class="text-grey-darkest text-sm leading-normal mb-4">
        <p class="pb-1"><b>Please note:</b>
        Videos uploaded here will be available to all users from your office.</p>
        <p class="pb-1">Custom logo animation files should fit the specification below:</p>
        <p class="pb-1"><b>Dimensions:</b> 1920px x 1080px. <b>Duration:</b> Exactly <span>{{ maxSeconds }}</span> seconds. <b>Frame Rate:</b> 25fps</p>
      </div>
      <form class="w-full flex flex-row">
        <div class="w-1/2 mr-3">
          <div
            class="relative flex flex-col items-center justify-center border border-grey rounded border-dashed"
            :class="{ 'h-80': !custom_animation.file }"
          >
            <button
              v-if="custom_animation.file"
              class="absolute p-1 pin-t pin-r cursor-pointer z-50"
              @click.prevent="removeFile"
            >
              <i class="fas fa-times fa-fw text-white"></i>
            </button>
            <input
              v-show="!custom_animation.file"
              type="file"
              class="input-file cursor-pointer absolute pin-t pin-l h-full w-full opacity-0"
              @change="loadVideo"
              ref="videoFile"
              accept="video/mp4,video/*"
            />
            <video
              v-show="custom_animation.file"
              controls
              id="videoLength"
              ref="customVideo"
              :src="videoUrl"
              class="pin-t pin-l h-full w-full"
            ></video>
            <button
              v-if="!custom_animation.file"
              class="bg-tertiary font-bold px-3 rounded mt-2 chooseFileButton inline-flex items-center justify-center cursor-pointer w-48 mb-2"
            >
              <icon
                name="videoettes"
                class="w-10 mr-3 text-white ico"
                :center="false"
              />
              <span class="text-sm font-thin text-primary text-white text-center">Choose file</span>
            </button>
            <p
              v-if="!custom_animation.file"
              class="call-to-action text-sm font-thin text-center leading-normal pt-4 pb-1"
            >
              Click or drag and drop your
              <br />intro animation video here
            </p>
          </div>
        </div>
        <div class="w-1/2 ml-3">
          <label for="animation_name" class="block mb-1 text-sm">Logo Animation Name:</label>
          <input type="text" v-model="custom_animation.name" placeholder="My Custom Animation #1" id="animation_name" class="input-sm bg-white w-full mb-4" required>
          <ul class="list-reset text-red-dark text-xs leading-normal mb-2" v-if="!isEmpty(errorBag.errors)">
            <li v-for="(error, i) in errorBag.errors" :key="i">{{ error.join(' ') }}</li>
          </ul>
          <div class="text-xs">
            <loader v-if="uploading"/>
            <button v-else class="text-sm rounded py-3 px-5 my-2" :class="saveReady ? 'bg-tertiary text-white cursor-pointer' : 'bg-grey-light text-grey-dark cursor-default'" :disabled="! saveReady" @click.prevent="uploadLogoAnimation">Save and Upload</button>
          </div>
        </div>
      </form>
    </div>
  </modal>
</template>

<script>
import { isEmpty } from 'lodash'
import { mapActions, mapState } from 'vuex'
import ErrorBag from '@/ErrorBag'
import api from '@/http'

export default {
  name: 'CustomLogoAnimationModal',

  props: ['showModal'],

  data () {
    return {
      custom_animation: {
        name: '',
        file: ''
      },
      videoUrl: null,
      errorBag: new ErrorBag(),
      maxSeconds: 5,
      saveReady: false,
      uploading: false
    }
  },

  computed: {
    ...mapState({
      selectedOffice: state => state.selectedOffice
    })
  },

  watch: {
    custom_animation: {
      handler () {
        this.saveReady = this.custom_animation.name !== '' && this.custom_animation.file !== ''
      },
      deep: true
    }
  },

  methods: {
    isEmpty,
    ...mapActions([
      'getVideoetteSettings'
    ]),
    async uploadLogoAnimation (e) {
      this.uploading = true
      this.errorBag.clear()
      let data = new FormData()
      data.append('video_file', this.custom_animation.file)
      data.append('name', this.custom_animation.name)
      data.append('office_id', this.selectedOffice.id)
      api.post('upload-logo-animation', data)
        .then((response) => {
          this.uploading = false
          this.$emit('uploaded')
          this.$emit('close')
          this.removeFile()
          this.getVideoetteSettings()
        }, (errors) => {
          this.uploading = false
          this.errorBag.store(errors.response.data)
        })
    },
    loadVideo () {
      this.custom_animation.file = this.$refs.videoFile.files[0]
      var type = this.custom_animation.file.type
      var videoNode = this.$refs.customVideo
      var canPlay = videoNode.canPlayType(type)
      if (canPlay === '') canPlay = 'no'
      this.videoUrl = URL.createObjectURL(this.custom_animation.file)
      this.validateFile()
    },
    validateFile () {
      var video = this.$refs.customVideo
      var fileType = this.custom_animation.file.type
      video.preload = 'metadata'
      video.onloadedmetadata = function () {
        var seconds = Math.round(video.duration)
        if (seconds !== this.maxSeconds || video.videoHeight !== 1080 || video.videoWidth !== 1920 || fileType !== 'video/mp4') {
          this.removeFile()
          var errorObj = {}
          if (fileType !== 'video/mp4') {
            errorObj.file = ['Video type must be MP4']
          }
          if (video.videoHeight !== 1080 || video.videoWidth !== 1920) {
            errorObj.dimension = ['Video dimension must be exactly 1920px X 1080px']
          }
          if (seconds !== this.maxSeconds) {
            errorObj.time = ['Video duration must be exactly ' + this.maxSeconds + ' seconds']
          }
          this.errorBag.store({
            errors: errorObj
          })
        } else {
          if (!this.custom_animation.name) {
            this.custom_animation.name = this.custom_animation.file.name
          }
          this.errorBag.clear()
        }
      }.bind(this)
    },
    removeFile () {
      this.$refs.customVideo.pause()
      this.custom_animation.file = ''
      this.custom_animation.name = ''
      this.$refs.videoFile.value = null
    }
  }
}
</script>
